/**
 * QuickSaleBar — a pinned scan/search bar for the dashboard.
 *
 * New Sale is ~90% of what the app does, so this lets a counter operator start
 * billing without a click: scan a barcode (or type a name/SKU) and hit Enter to
 * drop the item into the cart and jump to the billing screen.
 *
 * Scanner-robust: on Enter we run a direct exact barcode/SKU lookup rather than
 * trusting the debounced results list, because a hardware scanner types the
 * whole code + Enter faster than the 150 ms search debounce settles.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Q } from '@nozbe/watermelondb';
import { Plus, ScanLine } from 'lucide-react';
import { database } from '@/db';
import { Product } from '@/db/models/Inventory';
import { useAppStore } from '@/store/useAppStore';
import { useCartStore } from '@/store/useCartStore';
import { useObservable } from '@/hooks/useObservable';
import { toBillingProduct } from '@/billing/toBillingProduct';
import { formatCurrency } from '@/utils/format';
import { cn } from '@/utils/cn';

const MAX_RESULTS = 8;

export function QuickSaleBar() {
  const navigate = useNavigate();
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const addItem = useCartStore((s) => s.addItem);

  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [notFound, setNotFound] = useState(false);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounce the search term. Humans type slowly; scanners fire a burst.
  useEffect(() => {
    const t = setTimeout(() => setQuery(input.trim().toLowerCase()), 150);
    return () => clearTimeout(t);
  }, [input]);

  useEffect(() => setActiveIndex(0), [query]);
  useEffect(() => {
    if (input) setNotFound(false);
  }, [input]);
  useEffect(() => () => { if (blurTimer.current) clearTimeout(blurTimer.current); }, []);

  const results = useObservable<Product[]>(
    () => {
      if (!activeStoreId || query.length === 0) return null;
      const like = `%${query}%`;
      return database
        .get<Product>('products')
        .query(
          Q.where('store_id', activeStoreId),
          Q.where('is_deleted', false),
          Q.where('is_active', true),
          Q.or(
            Q.where('name', Q.like(like)),
            Q.where('barcode', Q.like(like)),
            Q.where('sku', Q.like(like)),
          ),
          Q.sortBy('name', Q.asc),
          Q.take(MAX_RESULTS),
        )
        .observe();
    },
    [activeStoreId, query],
    [],
  );

  function addAndGo(product: Product) {
    addItem(toBillingProduct(product));
    setInput('');
    setQuery('');
    setOpen(false);
    navigate('/billing');
  }

  async function commit() {
    const term = input.trim();
    if (!term || !activeStoreId) return;
    // Exact barcode/SKU first — reliable for fast hardware scanners.
    const exact = await database
      .get<Product>('products')
      .query(
        Q.where('store_id', activeStoreId),
        Q.where('is_deleted', false),
        Q.where('is_active', true),
        Q.or(Q.where('barcode', term), Q.where('sku', term)),
        Q.take(1),
      )
      .fetch();
    if (exact.length > 0) return addAndGo(exact[0]);
    if (results.length > 0) return addAndGo(results[Math.min(activeIndex, results.length - 1)]);
    setNotFound(true);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      void commit();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Escape') {
      setInput('');
      setQuery('');
      setOpen(false);
    }
  }

  const showDropdown = open && query.length > 0 && results.length > 0;

  return (
    <div className="relative">
      <div
        className={cn(
          'glass-surface flex items-center gap-3 rounded-lg border px-4 py-3 shadow-e1',
          'border-ink-200 transition-colors duration-120 focus-within:border-accent',
        )}
      >
        <ScanLine size={18} className="shrink-0 text-accent-ink" />
        <input
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            blurTimer.current = setTimeout(() => setOpen(false), 150);
          }}
          onKeyDown={onKeyDown}
          placeholder="Scan a barcode or search a product to start a sale…"
          className="min-w-0 flex-1 bg-transparent text-body text-fg-primary outline-none placeholder:text-fg-tertiary"
          aria-label="Quick sale — scan a barcode or search a product"
        />
        {notFound ? (
          <span className="shrink-0 text-label text-danger-ink">No match</span>
        ) : (
          <span className="shrink-0 text-label uppercase text-fg-tertiary">Enter to add</span>
        )}
      </div>

      {showDropdown && (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border border-stroke-hairline/15 bg-bg-surface shadow-e2">
          {results.map((p, i) => (
            <button
              key={p.id}
              type="button"
              // onMouseDown fires before the input's blur closes the list.
              onMouseDown={(e) => {
                e.preventDefault();
                if (blurTimer.current) clearTimeout(blurTimer.current);
                addAndGo(p);
              }}
              onMouseEnter={() => setActiveIndex(i)}
              className={cn(
                'flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left transition-colors duration-120',
                i === activeIndex ? 'bg-bg-subtle' : 'hover:bg-bg-subtle',
              )}
            >
              <div className="min-w-0">
                <p className="truncate text-body text-fg-primary">{p.name}</p>
                <p className="truncate text-label text-fg-tertiary">
                  {p.barcode || p.sku || '—'}
                  {p.trackInventory && !p.isService ? ` · ${p.currentStock} ${p.unit || ''} in stock` : ''}
                </p>
              </div>
              <span className="flex shrink-0 items-center gap-2">
                <span className="text-body font-semibold tabular-nums text-fg-primary">
                  {formatCurrency(p.sellingPrice)}
                </span>
                <Plus size={16} className="text-accent-ink" />
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
